import React from "react";
import { PackageCheckIcon, SparklesIcon, MapIcon, CheckIcon, ArrowRightIcon } from "lucide-react";
import { ButtonLink } from "../shared/Button";
import { SectionHeader } from "../shared/SectionHeader";
import { Reveal } from "../shared/Reveal";

// [Trust] [Conversion] Standard inclusions (published on /designs), spec levels and site costs (client-supplied).
// No "us vs big builders" table.
const costTypes = [{
  title: "Three specification levels",
  description: "Choose Core, Rounded or Elevated. Each lists exactly what you get, from ceiling heights and benchtops to appliances, flooring and insulation.",
  ask: "Stone benchtops and Westinghouse appliances are standard in every design.",
  icon: PackageCheckIcon
}, {
  title: "Optional upgrades",
  description: "Selections you choose to add, confirmed with our specialist at selections. Items in display photos aren’t automatically standard.",
  ask: "Every variation is priced and signed off before it goes ahead.",
  icon: SparklesIcon
}, {
  title: "Site costs",
  description: "The work to get your block ready: soil class, slope, retaining, sand fill, wind and BAL rating, coastal location and distance to services.",
  ask: "We arrange a soil test and contour survey early, so your quote reflects your actual block.",
  icon: MapIcon
}];
const checklist = [{
  title: "Scope",
  text: "Exactly what work is included, and what isn’t."
}, {
  title: "Inclusions",
  text: "The fixtures, finishes and fittings in the base price."
}, {
  title: "Exclusions",
  text: "Items you’d need to pay for separately."
}, {
  title: "Site costs",
  text: "Whether site works are estimated, allowed for or excluded."
}, {
  title: "Allowances",
  text: "Provisional sums and how differences are charged."
}, {
  title: "Variations",
  text: "How changes are requested, priced and approved."
}];
export function InclusionsClarity() {
  return <section id="inclusions" aria-labelledby="inclusions-heading" className="bg-paper py-20 md:py-28" data-id="element-265" data-mp="1vdli0vvz8fir">
      <div className="mx-auto max-w-site px-5 md:px-10" data-id="element-266" data-mp="2yo95ne47wmbz">
        <SectionHeader id="inclusions-heading" title="No surprises on price." intro="Fixed-price contracts, a detailed quote before you sign, and allowances kept to a minimum. Here’s what shapes your price, and what to check in any builder’s quote." data-id="element-267" data-mp="2yorc13klggxm" />

        <div className="mt-14 grid gap-6 md:grid-cols-3" data-id="element-268" data-mp="33m0ycacosch8">
          {costTypes.map((type, index) => <Reveal key={type.title} delay={index * 0.05} className="h-full" data-id="element-269" data-mp="3hliqmzp6y7vc" data-mpi={index}>
              <div className="flex h-full flex-col items-center rounded-[24px] border border-line bg-white p-7 text-center md:p-8" data-id="element-270" data-mp="3cnh8cgzlackx">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-paper text-charcoal" data-id="element-271" data-mp="3rf9afradhu4p">
                  <type.icon className="h-6 w-6" aria-hidden="true" data-id="element-272" data-mp="48gpmdm8og9v" />
                </span>
                <h3 className="mt-5 text-[22px] font-semibold tracking-tight" data-id="element-273" data-mp="464a073yehzz">{type.title}</h3>
                <p className="mt-3 text-[16px] leading-relaxed text-slate" data-id="element-274" data-mp="3vcozvni9vjvf">{type.description}</p>
                <p className="mt-auto w-full border-t border-line pt-4 text-[15px] font-medium text-charcoal" data-id="element-275" data-mp="it2xjqzows9f">{type.ask}</p>
              </div>
            </Reveal>)}
        </div>

        <Reveal className="mt-6" data-id="element-276" data-mp="9u7v8pzro2be">
          <div className="rounded-[32px] bg-charcoal p-7 text-paper md:p-12" data-id="element-277" data-mp="dvt13rdvvgc3">
            <h3 className="text-center text-[24px] font-semibold tracking-tight md:text-[30px]" data-id="element-278" data-mp="dxxf525hxvq3">Comparing quotes? Check these six things.</h3>
            <p className="mt-2 text-center text-[16px] text-paper/70" data-id="element-279" data-mp="4vo3g8yhrwb4">Useful whichever builder you’re talking to.</p>
            <ul className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-280" data-mp="3md7bruqma8qh">
              {checklist.map(item => <li key={item.title} className="flex items-start gap-4 rounded-2xl border border-paper/15 p-5" data-id="element-281" data-mp="3rb8u2dg7y40w">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-ocean text-charcoal" data-id="element-282" data-mp="3ddas67j3402a">
                    <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-283" data-mp="3a9bsqylimry5" />
                  </span>
                  <span data-id="element-284" data-mp="35as42qfjf223">
                    <span className="block font-heading text-[17px] font-semibold" data-id="element-285" data-mp="35a8ld7i2c9n0">{item.title}</span>
                    <span className="mt-1 block text-[15px] leading-relaxed text-paper/75" data-id="element-286" data-mp="30iqjmh87u50z">{item.text}</span>
                  </span>
                </li>)}
            </ul>
            <div className="mt-10 text-center" data-id="element-287" data-mp="2rjn23hop5xol">
              <ButtonLink to="/contact" data-id="element-288" data-mp="2rkexaredttec">
                Discuss pricing for your project <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-289" data-mp="2mj930p52py2p" />
              </ButtonLink>
            </div>
          </div>
        </Reveal>
      </div>
    </section>;
}